import React, { useContext } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Typography,
  Box,
  useTheme,
  Avatar,
  Divider,
  IconButton,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../store/AuthContext";
import {
  Dashboard,
  People,
  VideoLibrary,
  CloudUpload,
  Logout,
  MenuBook,
  AccountCircle,
  Close
} from "@mui/icons-material";

const drawerWidth = 280;

export default function TeacherSidebar({ open, onClose, isMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useContext(AuthContext);
  const theme = useTheme();

  const menuItems = [
    { text: "Dashboard", icon: <Dashboard />, path: "/teacher" },
    { text: "Topics", icon: <Dashboard />, path: "/teacher/topics" },
    { text: "Students", icon: <People />, path: "/teacher/students" },
    { text: "Videos", icon: <VideoLibrary />, path: "/teacher/videos" },
    { text: "Upload Video", icon: <CloudUpload />, path: "/teacher/upload-video" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose(); // Close sidebar on mobile after navigation
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        width: isMobile ? 280 : drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? 280 : drawerWidth,
          boxSizing: "border-box",
          background: "linear-gradient(180deg, #2c3e50 0%, #3498db 100%)",
          color: "white",
          border: "none",
          boxShadow: isMobile ? "4px 0 40px rgba(0,0,0,0.3)" : "4px 0 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      
      {/* Header */}
      <Toolbar sx={{ 
        pt: isMobile ? 0 : 2,
        background: "rgba(255,255,255,0.1)", 
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.1)"
      }}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <>
          <MenuBook sx={{ mr: 2, fontSize: 32, color: "white" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Teacher Panel
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
              Learning Management System
            </Typography>
          </Box>
          </>
          {/* Close Button for Mobile */}
      {isMobile && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton
            onClick={onClose}
            sx={{
              color: "white",
              background: "rgba(255,255,255,0.1)",
              "&:hover": {
                background: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      )}
        </Box>
      </Toolbar>

      {/* User Info */}
      <Box sx={{ p: 2, textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            mx: "auto",
            mb: 1,
            background: "linear-gradient(45deg, #FF6B6B, #4ECDC4)",
            border: "3px solid rgba(255,255,255,0.2)"
          }}
        >
          <AccountCircle sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: { xs: '1rem', md: '1.1rem' } }}>
          {user?.name || "Teacher"}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
          {user?.email || "teacher@school.com"}
        </Typography>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ overflow: "auto", flex: 1, p: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{
                mb: 1,
                borderRadius: 2,
                background: isActive(item.path) 
                  ? "linear-gradient(45deg, #FF6B6B, #4ECDC4)" 
                  : "transparent",
                "&:hover": {
                  background: isActive(item.path)
                    ? "linear-gradient(45deg, #FF6B6B, #4ECDC4)"
                    : "rgba(255,255,255,0.1)",
                  transform: "translateX(5px)",
                },
                transition: "all 0.3s ease",
                border: isActive(item.path) ? "none" : "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: isActive(item.path) ? "bold" : "normal"
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            background: "rgba(255,255,255,0.1)",
            "&:hover": {
              background: "rgba(255,107,107,0.8)",
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s ease",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <ListItemIcon sx={{ color: "white" }}>
            <Logout />
          </ListItemIcon>
          <ListItemText 
            primary="Logout" 
            primaryTypographyProps={{ 
              fontSize: { xs: '0.9rem', md: '1rem' },
              fontWeight: "bold"
            }}
          />
        </ListItemButton>
      </Box>
    </Drawer>
  );
}